/**
 * 图片加载优化工具类
 * 避免重复加载失败的图片资源
 */

// 存储已加载失败的图片URL
const failedImages = new Set()

// 默认图片配置
const DEFAULT_IMAGES = {
  product: '/images/product-default.jpg',
  news: '/images/news-default.jpg',
  company: '/images/company-default.jpg',
  avatar: '/images/avatar-default.jpg'
}

/**
 * 检查图片URL是否有效
 * @param {string} url 图片URL
 * @returns {Promise<boolean>} 是否有效
 */
export function checkImageUrl (url) {
  if (!url || failedImages.has(url)) {
    return Promise.resolve(false)
  }

  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve(true)
    img.onerror = () => {
      failedImages.add(url)
      resolve(false)
    }
    img.src = url
  })
}

/**
 * 获取安全的图片URL
 * @param {string} url 原始图片URL
 * @param {string} type 图片类型 (product, news, company, avatar)
 * @returns {string} 安全的图片URL
 */
export function getSafeImageUrl (url, type = 'product') {
  if (!url || failedImages.has(url)) {
    return DEFAULT_IMAGES[type] || DEFAULT_IMAGES.product
  }
  return url
}

/**
 * 预加载图片
 * @param {string} url 图片URL
 * @returns {Promise<string>} 返回可用的图片URL
 */
export async function preloadImage (url, type = 'product') {
  if (!url) {
    return DEFAULT_IMAGES[type] || DEFAULT_IMAGES.product
  }

  if (failedImages.has(url)) {
    return DEFAULT_IMAGES[type] || DEFAULT_IMAGES.product
  }

  try {
    const isValid = await checkImageUrl(url)
    if (isValid) {
      return url
    } else {
      return DEFAULT_IMAGES[type] || DEFAULT_IMAGES.product
    }
  } catch (error) {
    console.warn('图片加载失败:', url, error)
    return DEFAULT_IMAGES[type] || DEFAULT_IMAGES.product
  }
}

/**
 * 批量预加载图片
 * @param {Array} urls 图片URL数组
 * @param {string} type 图片类型
 * @returns {Promise<Array>} 返回可用的图片URL数组
 */
export async function preloadImages (urls, type = 'product') {
  if (!Array.isArray(urls)) {
    return []
  }

  const promises = urls.map(url => preloadImage(url, type))
  return Promise.all(promises)
}

/**
 * 图片加载失败的处理器
 * @param {Event} event 错误事件
 * @param {string} type 图片类型
 * @returns {string} 默认图片URL
 */
export function handleImageError (event, type = 'product') {
  const img = event.target

  // 避免无限重试：如果该元素已经加载过默认图片失败，则不再重试
  if (img.dataset.errorRetried) {
    return img.src
  }
  img.dataset.errorRetried = 'true'

  const originalSrc = img.src

  // 记录失败的图片URL
  if (originalSrc && !originalSrc.includes('default')) {
    failedImages.add(originalSrc)
  }

  // 设置默认图片
  const defaultSrc = DEFAULT_IMAGES[type] || DEFAULT_IMAGES.product
  img.src = defaultSrc

  return defaultSrc
}

/**
 * 清除失败的图片缓存
 */
export function clearFailedImageCache () {
  failedImages.clear()
}

/**
 * 获取失败的图片列表
 * @returns {Array} 失败的图片URL数组
 */
export function getFailedImages () {
  return Array.from(failedImages)
}

/**
 * 图片懒加载指令
 */
export const lazyLoadDirective = {
  bind (el, binding) {
    const { value } = binding
    const { src, type = 'product', placeholder } = value || {}

    if (!src) {
      el.src = DEFAULT_IMAGES[type] || DEFAULT_IMAGES.product
      return
    }

    // 设置占位符
    if (placeholder) {
      el.src = placeholder
    }

    // 创建观察器
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          preloadImage(src, type).then(safeUrl => {
            el.src = safeUrl
          })
          observer.unobserve(el)
        }
      })
    })

    observer.observe(el)
  }
}

/**
 * 图片组件混入
 */
export const imageMixin = {
  data () {
    return {
      imageLoading: false,
      imageError: false
    }
  },
  methods: {
    async loadImage (url, type = 'product') {
      if (!url) {
        return DEFAULT_IMAGES[type] || DEFAULT_IMAGES.product
      }

      this.imageLoading = true
      this.imageError = false

      try {
        const safeUrl = await preloadImage(url, type)
        this.imageLoading = false
        return safeUrl
      } catch (error) {
        this.imageLoading = false
        this.imageError = true
        console.warn('图片加载失败:', url, error)
        return DEFAULT_IMAGES[type] || DEFAULT_IMAGES.product
      }
    },

    handleImageError (event, type = 'product') {
      this.imageError = true
      return handleImageError(event, type)
    }
  }
}
